import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import { get } from "lodash";

import { Button } from "@treelab/panda";

import { setAppConfig, getAppConfig } from "../../network/index";

import { IChartFormValue, IFormValue } from "../../types/form-type";
import {
  IQueryProps,
  GlobalErrorType,
  PostMessageEventName,
  IPostMessage,
  AppType,
  ChartType,
} from "../../types/global-type";
import { ITableData, IWrokspaceData } from "../../types/table-type";

import { GlobalErrorMessageId } from "../../constants/app";

import ChartView from "../../components/ChartView";
import Summary from "../../components/Summary";
import Loading from "../../components/Loading";
import ChartForm from "../../components/ChartForm";
import Modal, { IModal } from "../../components/Modal";
import NotPermission from "../../components/NotPermission";
import ErrorModal from "../../components/Error";

import { getFormDefaultValue, parsingAppConfig } from "../../utils/form_main";
import { checkTablePermission, sortColumnsAndRows } from "../../utils/tableData_main";
import { getAppType } from "../../utils/app";

import { formatMessage } from "../../lib/intl";

import { Container, ChartContainer, FormContainer, FixedButtonContainer } from "./styled";

interface IProps {
  query: IQueryProps;
}

const EditPage: React.FC<IProps> = ({ query }) => {
  const { tableId, token, blockId, isExpand, hasPermission, sessionId, form, locale, shareUserId } = query;
  // @ts-ignore
  const urlParams = useParams();
  const { appType: appTypeStr } = urlParams as { appType: AppType };
  const appType = getAppType(appTypeStr);

  const [tableData, setTableData] = useState<ITableData | null>();
  const [workspaceData, setWorkspaceData] = useState<IWrokspaceData | null>();
  const [value, setValue] = useState<IFormValue>();
  const [showConfirmModal, setShowConfirmModal] = useState<IModal | null>();
  const [isChange, setIsChange] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [isShowForm, setIsShowForm] = useState<boolean>(hasPermission);
  const [isAppConfigResolved, setIsAppConfigResolved] = useState<boolean>(false);

  /**
   * 当第一次请求 getAppConfig 得到返回值后，且 tableData 有值, 会判断是否需要计算 默认的 formValue
   */
  useEffect(() => {
    if (!isAppConfigResolved) return;
    if (tableData && !value) {
      setValue(getFormDefaultValue(tableData, appType));
    }
  }, [value, tableData, appType, isAppConfigResolved]);

  const getData = useCallback(() => {
    setIsAppConfigResolved(false);
    getAppConfig(blockId, token, sessionId)
      .then((data) => {
        const appConfigResult = parsingAppConfig(data);
        if (!appConfigResult || appConfigResult.tableId !== tableId) {
        } else {
          if (appType === AppType.CHART) {
            const newFormValue = {
              ...appConfigResult?.formValue,
            } as IFormValue<AppType.CHART>;
            if (!newFormValue.chartType) newFormValue.chartType = ChartType.BASIC_BAR;
            setValue(newFormValue);
          } else {
            setValue(appConfigResult?.formValue);
          }
        }
        setIsAppConfigResolved(true);
        setIsChange(false);
      })
      .catch((err) => {
        setError(get(err, "[0].extensions.code"));
      });
  }, [tableId, token, blockId, appType, sessionId]);

  const loadTableData = useCallback((tableResult?: ITableData) => {
    tableResult && sortColumnsAndRows(tableResult);
    setTableData(tableResult);
  }, []);

  const loadWorkspace = useCallback(
    (workspaceResult?: IWrokspaceData) => {
      if (!workspaceResult) {
        setError(
          formatMessage({
            id: GlobalErrorMessageId[GlobalErrorType.RESPONSE_ERROR],
          })
        );
      } else {
        if (isShowForm) {
          setIsShowForm(checkTablePermission(workspaceResult, tableId));
        }
      }
      setWorkspaceData(workspaceResult);
      const treelab = {
        users: workspaceResult?.users,
      };
      // @ts-ignore
      window.treelab = treelab;
    },
    [isShowForm, tableId]
  );

  const putFormValue = useCallback(() => {
    const appConfig = {
      tableId,
      formValue: value,
    };
    setIsChange(false);
    return setAppConfig(blockId, appConfig, token, sessionId);
  }, [value, blockId, token, sessionId, tableId]);

  const postMessageFunc = useCallback((eventName: PostMessageEventName, data?: any) => {
    window.parent.postMessage({ eventName, data: data }, "*");
  }, []);

  const handleConfirmModalClose = useCallback(() => {
    setShowConfirmModal(null);
  }, []);

  const handleCloseModalSubmit = useCallback(() => {
    handleConfirmModalClose();
    putFormValue()
      .then(() => {
        postMessageFunc(PostMessageEventName.syncBlockAppConfig, { blockId: blockId });
        postMessageFunc(PostMessageEventName.closeModal, {
          isFromSaveConfig: true,
          blockId: blockId,
        });
      })
      .catch(() => {});
  }, [putFormValue, postMessageFunc, blockId, handleConfirmModalClose]);

  const handleSaveRefreshModalSubmit = useCallback(() => {
    handleConfirmModalClose();
    putFormValue()
      .then(() => {
        postMessageFunc(PostMessageEventName.syncBlockAppConfig, { blockId: blockId });
        getData();
      })
      .catch(() => {});
  }, [handleConfirmModalClose, getData, blockId, postMessageFunc, putFormValue]);

  const handleCloseModalReject = useCallback(() => {
    postMessageFunc(PostMessageEventName.closeModal);
    handleConfirmModalClose();
  }, [postMessageFunc, handleConfirmModalClose]);

  const handleSaveRefreshModalReject = useCallback(() => {
    getData();
    handleConfirmModalClose();
  }, [getData, handleConfirmModalClose]);

  // 获取 workspace table 数据
  useEffect(() => {
    try {
      if (!token) return;
      getData();
    } catch (error) {}
  }, [token, getData]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const { data: eventData, origin } = event;
      const { eventName, data } = eventData as IPostMessage;
      switch (eventName) {
        case PostMessageEventName.closeModalCheck:
          if (isChange) {
            setShowConfirmModal({
              titleId: "form.modal.confirm.title",
              messageId: "form.modal.confirm.message.close.modal",
              onClose: handleConfirmModalClose,
              onSubmit: handleCloseModalSubmit,
              onReject: handleCloseModalReject,
            });
          } else {
            if (window.parent !== window) {
              window.parent.postMessage({ eventName: PostMessageEventName.closeModal }, origin);
            }
          }
          break;
        case PostMessageEventName.refreshAppConfig:
          getData();
          break;
        case PostMessageEventName.refreshModalCheck:
          if (!isChange) {
            getData();
          } else {
            setShowConfirmModal({
              titleId: "form.modal.confirm.title",
              messageId: "form.modal.confirm.message.refresh",
              onClose: handleConfirmModalClose,
              onReject: handleSaveRefreshModalReject,
              onSubmit: handleSaveRefreshModalSubmit,
            });
          }
          break;
        case PostMessageEventName.pushTableData:
          loadTableData(data);
          break;
        case PostMessageEventName.pushWorkspaceData:
          loadWorkspace(data);
          break;
      }
    },
    [
      isChange,
      getData,
      loadTableData,
      loadWorkspace,
      handleConfirmModalClose,
      handleCloseModalSubmit,
      handleCloseModalReject,
      handleSaveRefreshModalReject,
      handleSaveRefreshModalSubmit,
    ]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage, false);
    window.parent.postMessage({ eventName: PostMessageEventName.sendReadyForData, data: { blockId, isExpand } }, "*");
    return () => {
      window.removeEventListener("message", handleMessage, false);
    };
  }, [blockId, handleMessage, isExpand]);

  const handleChange = useCallback(
    (value) => {
      setValue(value);
      setIsChange(true);
    },
    [setValue, setIsChange]
  );

  const handleSubmit = useCallback(() => {
    putFormValue()
      .then(() => {
        postMessageFunc(PostMessageEventName.syncBlockAppConfig, {
          blockId: blockId,
        });
        postMessageFunc(PostMessageEventName.closeModal, {
          isFromSaveConfig: true,
          blockId: blockId,
        });
      })
      .catch(() => {});
  }, [putFormValue, postMessageFunc, blockId]);

  const handleViewLond = useCallback(() => {
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          eventName: PostMessageEventName.sendReadyStatus,
          data: { blockId, isExpand },
        },
        "*"
      );
    }
  }, [blockId, isExpand]);

  if (error) {
    return <ErrorModal errorMessage={error} />;
  }

  if (!value || !workspaceData || !tableData) {
    return <Loading />;
  }

  return (
    <Container>
      {!!showConfirmModal && <Modal {...showConfirmModal} />}
      <ChartContainer>
        {appType === AppType.CHART && (
          <ChartView
            workspaceData={workspaceData}
            tableData={tableData}
            formValue={value as IChartFormValue}
            from={form}
            onload={handleViewLond}
            isExpand={isExpand}
          />
        )}
        {appType === AppType.AGGREGATE && (
          <Summary
            tableData={tableData}
            formValue={value as IFormValue<AppType.AGGREGATE>}
            onload={handleViewLond}
            isExpand={isExpand}
            locale={locale}
          />
        )}
      </ChartContainer>
      <FormContainer>
        {isShowForm ? (
          <>
            <ChartForm
              value={value as IChartFormValue}
              onChange={handleChange}
              tableData={tableData}
              appType={appType}
              shareUserId={shareUserId}
            />
          </>
        ) : (
          <NotPermission />
        )}
        <FixedButtonContainer>
          <Button onClick={handleSubmit} itemSize="large" isDisabled={!hasPermission}>
            <FormattedMessage id="global.confirm" />
          </Button>
        </FixedButtonContainer>
      </FormContainer>
    </Container>
  );
};

export default EditPage;
